import { useState, useEffect, FormEvent } from 'react'
import emailjs from '@emailjs/browser'

// EmailJS Configuration
const EMAILJS_SERVICE_ID = 'service_fhwace2'
const EMAILJS_PUBLIC_KEY = 'IB5j2dmRSF9DxPdIe'
const EMAILJS_TEMPLATE_ID = 'template_z3sq9uc'

// Food emoji confetti items
const FOOD_EMOJIS = ['🍗', '🥘', '🍚', '🌽', '🥬', '🍠', '🫘', '🧅', '🍞', '🥧', '🍰', '🥗', '🍲', '🌶️', '🥜']

interface ConfettiPiece {
  id: number
  emoji: string
  left: number
  delay: number
  duration: number
  size: number
}

const EmailPopup = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [confetti, setConfetti] = useState<ConfettiPiece[]>([])

  useEffect(() => {
    // Check if user has already seen popup
    const hasSeenPopup = localStorage.getItem('blackplate_popup_seen')
    
    if (!hasSeenPopup) {
      // Show popup after 5 seconds for first-time visitors
      const timer = setTimeout(() => {
        setIsVisible(true)
      }, 5000)
      
      return () => clearTimeout(timer)
    }
  }, [])

  const generateConfetti = () => {
    const pieces: ConfettiPiece[] = []
    for (let i = 0; i < 50; i++) {
      pieces.push({
        id: i,
        emoji: FOOD_EMOJIS[Math.floor(Math.random() * FOOD_EMOJIS.length)],
        left: Math.random() * 100,
        delay: Math.random() * 0.5,
        duration: 2 + Math.random() * 2,
        size: 20 + Math.random() * 20,
      })
    }
    setConfetti(pieces)
    
    // Clear confetti after animation
    setTimeout(() => setConfetti([]), 4000)
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!email) return

    setIsSubmitting(true)

    try {
      // Submit to Netlify Forms
      const formBody = new URLSearchParams()
      formBody.append('form-name', 'pass-the-plate')
      formBody.append('email', email)
      formBody.append('source', 'popup')
      formBody.append('timestamp', new Date().toISOString())

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      })

      if (response.ok) {
        // Send EmailJS notification
        await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          {
            businessName: '🍽️ PASS THE PLATE SIGNUP',
            category: 'Newsletter Subscription',
            city: 'Welcome Popup',
            state: 'Website',
            zip: '',
            address: '',
            phone: '',
            email: email,
            website: '',
            instagram: '',
            facebook: '',
            additionalInfo: `New subscriber joined via the "Pass the Plate" welcome popup!\n\nEmail: ${email}\nSource: Pass the Plate Popup\nTime: ${new Date().toLocaleString()}`,
            newsletter: 'Yes - Pass the Plate Signup',
          },
          EMAILJS_PUBLIC_KEY
        )

        setIsSuccess(true)
        generateConfetti()
        localStorage.setItem('blackplate_popup_seen', 'true')
        
        // Close popup after celebration
        setTimeout(() => {
          setIsVisible(false)
        }, 4000)
      } else {
        throw new Error('Submission failed')
      }
    } catch {
      // Even on error, mark as seen so we don't annoy the user
      localStorage.setItem('blackplate_popup_seen', 'true')
      setIsVisible(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    localStorage.setItem('blackplate_popup_seen', 'true')
    setIsVisible(false)
  }

  if (!isVisible) return null

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] animate-fade-in"
        onClick={handleClose}
      />
      
      {/* Food Confetti */}
      {confetti.map((piece) => (
        <div
          key={piece.id}
          className="fixed z-[102] pointer-events-none animate-confetti-fall"
          style={{
            left: `${piece.left}%`,
            top: '-50px',
            fontSize: `${piece.size}px`,
            animationDelay: `${piece.delay}s`,
            animationDuration: `${piece.duration}s`,
          }}
        >
          {piece.emoji}
        </div>
      ))}

      {/* Modal */}
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full pointer-events-auto transform animate-popup-scale overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with pattern */}
          <div className="bg-gradient-to-r from-rich-mahogany to-burnt-sienna p-6 relative overflow-hidden">
            {/* Decorative pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M20 20h20v20H20V20zm0-20h20v20H20V0zM0 20h20v20H0V20zM0 0h20v20H0V0z'/%3E%3C/g%3E%3C/svg%3E")`,
              }} />
            </div>
            
            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-warm-cream/70 hover:text-warm-cream transition-colors"
              aria-label="Close popup"
            >
              <i className="fas fa-times text-xl" aria-hidden="true"></i>
            </button>

            {/* Icon */}
            <div className="w-16 h-16 bg-golden-amber rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <span className="text-3xl">🍽️</span>
            </div>

            <h2 className="text-2xl md:text-3xl font-crimson font-bold text-warm-cream text-center">
              Pass the Plate
            </h2>
            <p className="text-golden-amber text-center mt-2 text-sm">
              Join our community table
            </p>
          </div>

          {/* Content */}
          <div className="p-6">
            {!isSuccess ? (
              <>
                <p className="text-gray-600 text-center mb-6">
                  Get updates on new Black-owned food businesses, heritage recipes, 
                  and soul food stories delivered to your inbox.
                </p>

                {/* Form */}
                <form
                  name="pass-the-plate"
                  method="POST"
                  data-netlify="true"
                  netlify-honeypot="bot-field"
                  onSubmit={handleSubmit}
                >
                  <input type="hidden" name="form-name" value="pass-the-plate" />
                  <p className="hidden">
                    <label>Don't fill this out: <input name="bot-field" /></label>
                  </p>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                      className="flex-1 px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-terracotta focus:outline-none text-gray-700"
                    />
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-terracotta hover:bg-burnt-sienna disabled:opacity-50 text-white font-bold py-3 px-6 rounded-lg transition-all whitespace-nowrap"
                    >
                      {isSubmitting ? (
                        <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                      ) : (
                        <>
                          <i className="fas fa-utensils mr-2" aria-hidden="true"></i>
                          Join Us
                        </>
                      )}
                    </button>
                  </div>
                </form>

                <p className="text-gray-400 text-xs text-center mt-4">
                  <i className="fas fa-lock mr-1" aria-hidden="true"></i>
                  We respect your privacy. Unsubscribe anytime.
                </p>
              </>
            ) : (
              /* Success State */
              <div className="text-center py-4">
                <div className="w-20 h-20 bg-forest-green rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                  <span className="text-4xl">🎉</span>
                </div>
                <h3 className="text-2xl font-crimson font-bold text-rich-mahogany mb-2">
                  Welcome to the Table!
                </h3>
                <p className="text-gray-600">
                  You're now part of The Black Plate family. 
                  <br />
                  <span className="text-terracotta font-semibold">Good food is coming your way!</span>
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-soft-linen px-6 py-4 border-t border-gray-100">
            <p className="text-center text-xs text-gray-500 italic">
              "Every meal shared is a tradition preserved."
            </p>
          </div>
        </div>
      </div>
    </>
  )
}

export default EmailPopup
